<template>
  <!-- Renders nothing once the visitor is confirmed, and nothing at all
       when the tenant has no newsletter — an inert signup box on a shop
       that never sends anything is just noise. -->
  <div v-if="visible" class="nl-box" :class="variant">
    <div class="nl-copy">
      <p class="nl-title">{{ title }}</p>
      <p class="nl-sub">{{ subtitle }}</p>
    </div>

    <form class="nl-form" @submit.prevent="submit">
      <input
        v-if="!loggedIn"
        v-model="email"
        type="email"
        required
        :placeholder="$t('shop.newsletter_placeholder', 'you@example.com')"
        class="nl-input"
        :disabled="busy"
      />
      <button type="submit" class="nl-btn" :disabled="busy">
        {{ busy ? $t('shop.newsletter_sending', 'Sending…') : $t('shop.newsletter_cta', 'Subscribe') }}
      </button>
    </form>

    <p v-if="sent" class="nl-done">
      {{ $t('shop.newsletter_check_inbox', 'Check your inbox and click the confirmation link.') }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
/**
 * Shop newsletter signup.
 *
 * Extracted into a component because the list needs more than one
 * entry point: before this existed the ONLY way to subscribe was a box
 * on /free that appeared solely when an admin had gated a pool item —
 * so a tenant could switch the newsletter on and still have no surface
 * for anyone to join it.
 */
const props = defineProps({
  /** 'footer' is the compact always-on variant; 'panel' is the /free page. */
  variant: { type: String, default: 'footer' },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  source: { type: String, default: 'footer' }
})

const { $toast } = useNuxtApp()
const { loggedIn } = useUserSession()

const email = ref('')
const busy = ref(false)
const sent = ref(false)
const subscribed = ref(false)
const checked = ref(false)

// Hidden until we know the visitor is not already on the list, so a
// confirmed subscriber never sees "subscribe" on every page.
const visible = computed(() => checked.value && !subscribed.value && !sent.value)

onMounted(async () => {
  try {
    const res = await $fetch('/api/shop/newsletter/status')
    subscribed.value = !!res.subscribed
  } catch {
    // Signed out or lookup failed — show the box; the endpoint is the
    // authority and will simply re-issue a confirmation.
  } finally {
    checked.value = true
  }
})

async function submit() {
  busy.value = true
  try {
    await $fetch('/api/shop/newsletter/subscribe', {
      method: 'POST',
      body: { email: email.value || undefined, source: props.source }
    })
    sent.value = true
    email.value = ''
    $toast?.success('Check your inbox for the confirmation link.')
  } catch (e) {
    $toast?.error(e?.data?.statusMessage || 'Could not subscribe')
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.nl-box { display: flex; gap: 0.75rem; }

.nl-box.footer {
  flex-direction: column;
  align-items: flex-start;
}
/* The panel variant is the wide strip on the shop pages — it gets the
   generated horizon plate instead of a flat surface. The scrim keeps it
   near the module tone so the strip still reads as part of the console,
   not as a photo pasted into the page. The footer variant stays flat: it
   sits inside the footer's own ground. */
.nl-box.panel {
  align-items: center; justify-content: space-between; flex-wrap: wrap;
  padding: 0.9rem 1.15rem; margin-bottom: 1rem;
  background-image:
    linear-gradient(to right, rgb(3 3 4 / 0.92), rgb(3 3 4 / 0.72)),
    url('/images/cta-strip.webp');
  background-size: cover, cover;
  background-position: center, center bottom;
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
}

.nl-title { color: var(--text-main); font-family: var(--cy-display); font-weight: 700; font-size: 0.85rem; letter-spacing: 0.04em; text-transform: uppercase; }
.nl-box.footer .nl-title { font-size: 0.8rem; }
.nl-sub { color: var(--text-muted); font-size: 0.75rem; }

.nl-form { display: flex; gap: 0.5rem; flex-wrap: wrap; }

.nl-input {
  padding: 0.55rem 0.75rem; border-radius: 3px;
  background: color-mix(in srgb, var(--cy-ground) 60%, transparent);
  border: 1px solid var(--cy-rim);
  color: var(--text-main); font-family: var(--cy-mono); font-size: 0.78rem; min-width: 180px;
  transition: border-color 0.15s;
}
.nl-input:focus { outline: none; border-color: var(--cy-rim-hot); }
.nl-input::placeholder { color: var(--text-dim); }
.nl-input:disabled { opacity: 0.5; }

.nl-btn {
  padding: 0.55rem 1rem; border-radius: 3px;
  background: var(--primary); color: var(--cy-ground);
  font-family: var(--cy-display);
  font-size: 0.72rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  white-space: nowrap;
  filter: drop-shadow(0 0 10px var(--cy-glow-c));
}
.nl-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.nl-done { color: var(--success); font-family: var(--cy-mono); font-size: 0.72rem; }
</style>
